import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bookingAPI, companyAPI, adminAPI } from '../services/api';
import Layout from '../components/Layout';
import Icon from '../components/Icon';
import EnterpriseLockedOverlay from '../components/EnterpriseLockedOverlay';
import BikePassportModal from '../components/BikePassportModal';


// Status colors - consolidated green palette
const STATUS_COLORS = {
  pending: { 
    bg: 'rgba(245, 158, 11, 0.15)', 
    bgLight: 'rgba(245, 158, 11, 0.08)',
    border: '#f59e0b', 
    text: '#fbbf24',
    textLight: '#d97706'
  },
  confirmed: { 
    bg: 'rgba(59, 130, 246, 0.15)', 
    bgLight: 'rgba(59, 130, 246, 0.08)',
    border: '#3b82f6', 
    text: '#60a5fa',
    textLight: '#3b82f6'
  },
  in_progress: { 
    bg: 'rgba(78, 205, 196, 0.15)', 
    bgLight: 'rgba(78, 205, 196, 0.08)',
    border: '#4ecdc4', 
    text: '#4ecdc4',
    textLight: '#22c1c3'
  },
  completed: { 
    bg: 'rgba(26, 40, 17, 0.15)', 
    bgLight: 'rgba(26, 40, 17, 0.08)',
    border: '#1A2811', 
    text: '#4A8C18',
    textLight: '#1A2811'
  },
  cancelled: { 
    bg: 'rgba(239, 68, 68, 0.15)', 
    bgLight: 'rgba(239, 68, 68, 0.08)',
    border: '#ef4444', 
    text: '#f87171',
    textLight: '#dc2626'
  },
};

const isSquarePlaceholderVin = (vin) => (vin || '').toString().trim().toLowerCase().startsWith('sq-ph-');

function CompanyBookingsCalendar({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'table'
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState('week'); // 'week', 'month', 'all'
  const [bikeFilter, setBikeFilter] = useState(null); // Filter by bike_id when clicking on booking
  const [passportBike, setPassportBike] = useState(null);
  
  // For editing booking details
  const [bikes, setBikes] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnicianFilter, setSelectedTechnicianFilter] = useState('');
  const [editingBooking, setEditingBooking] = useState(null);
  const [editTemplateId, setEditTemplateId] = useState('');
  const [editBookingDate, setEditBookingDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [savingBooking, setSavingBooking] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [newBookingDateTime, setNewBookingDateTime] = useState(null);
  const [showRecordServiceModal, setShowRecordServiceModal] = useState(false);
  const [serviceFormData, setServiceFormData] = useState({
    description: '',
    total_cost: 0,
    service_date: new Date().toISOString().split('T')[0],
    service_time: '09:00'
  });
  
  // New booking form state
  const [newBookingFormData, setNewBookingFormData] = useState({
    bike_id: '',
    template_id: '',
    technician_id: '',
    start_time: '09:00',
    duration_minutes: 60,
    notes: ''
  });
  
  // Catch-up day feature
  const [showCatchUpModal, setShowCatchUpModal] = useState(false);
  const [catchUpDate, setCatchUpDate] = useState(new Date().toISOString().split('T')[0]);
  const [pendingServices, setPendingServices] = useState([]);
  const [selectedPendingServices, setSelectedPendingServices] = useState([]);
  const [loadingPendingServices, setLoadingPendingServices] = useState(false);
  const [creatingCatchUp, setCreatingCatchUp] = useState(false);
  const [catchUpDays, setCatchUpDays] = useState([]);

  // Mobile day view
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [mobileSelectedDay, setMobileSelectedDay] = useState(new Date());
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Detect mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Touch handlers for swipe navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // Go to next day
      const nextDay = new Date(mobileSelectedDay);
      nextDay.setDate(nextDay.getDate() + 1);
      setMobileSelectedDay(nextDay);
    }
    if (isRightSwipe) {
      // Go to previous day
      const prevDay = new Date(mobileSelectedDay);
      prevDay.setDate(prevDay.getDate() - 1);
      setMobileSelectedDay(prevDay);
    }
  };

  const goToMobilePrevDay = () => {
    const prevDay = new Date(mobileSelectedDay);
    prevDay.setDate(prevDay.getDate() - 1);
    setMobileSelectedDay(prevDay);
  };

  const goToMobileNextDay = () => {
    const nextDay = new Date(mobileSelectedDay);
    nextDay.setDate(nextDay.getDate() + 1);
    setMobileSelectedDay(nextDay);
  };

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function getWeekDays(weekStart) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }

  useEffect(() => {
    fetchData();
  }, [currentWeekStart, currentMonth, statusFilter, dateRange, selectedTechnicianFilter, bikeFilter]);

  // Fetch bikes and templates when company is loaded
  useEffect(() => {
    if (company?.id) {
      fetchBikesAndTemplates();
    }
  }, [company]);

  // Handle URL parameter for booking selection
  useEffect(() => {
    const bookingIdParam = searchParams.get('bookingId');
    const dateParam = searchParams.get('date');
    
    // Handle date parameter first (if provided)
    if (dateParam) {
      try {
        const targetDate = new Date(dateParam);
        if (!isNaN(targetDate.getTime())) {
          setCurrentWeekStart(getWeekStart(targetDate));
          setCurrentMonth(targetDate);
        }
      } catch (e) {
        console.log('Invalid date parameter');
      }
    }
    
    // Then handle booking selection
    if (bookingIdParam && bookings.length > 0) {
      const bookingId = parseInt(bookingIdParam);
      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        setSelectedBooking(booking);
        // Set the week to show the booking's date (if date param wasn't provided)
        if (!dateParam) {
          const bookingDate = new Date(booking.booking_date);
          setCurrentWeekStart(getWeekStart(bookingDate));
          setCurrentMonth(bookingDate);
        }
        // Remove the parameters from URL after selection
        searchParams.delete('bookingId');
        searchParams.delete('date');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [bookings, searchParams, setSearchParams]);

  const fetchBikesAndTemplates = async () => {
    try {
      const [bikesResp, templatesResp, techniciansResp] = await Promise.all([
        adminAPI.getAllBikes(),
        bookingAPI.getServiceTemplates(false, company.id),
        bookingAPI.getTechnicians(true, company.id)
      ]);
      setBikes(bikesResp.data.bikes || []);
      setServiceTemplates(templatesResp.data.templates || []);
      setTechnicians(techniciansResp.data.technicians || []);
    } catch (err) {
      console.error('Error fetching bikes/templates/technicians:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const companyResp = await companyAPI.getCompanyInfo();
      const companyData = companyResp.data.company;
      setCompany(companyData);
      
      // Feature gating: Calendar/Booking is Enterprise-only (Pro/Basic see locked overlay)
      const tier = (companyData?.subscription_tier || '').toLowerCase();
      if (tier !== 'enterprise' && tier !== 'lifetime') {
        setLoading(false);
        return; // Don't fetch bookings; show locked overlay
      }

      // Calculate date range based on dateRange setting
      let dateFrom = null;
      let dateTo = null;
      
      if (dateRange === 'week') {
        dateFrom = currentWeekStart.toISOString().split('T')[0];
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        dateTo = weekEnd.toISOString().split('T')[0];
      } else if (dateRange === 'month') {
        const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
        dateFrom = firstDay.toISOString().split('T')[0];
        dateTo = lastDay.toISOString().split('T')[0];
      }

      const filters = {};
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;
      if (statusFilter) filters.status = statusFilter;
      if (selectedTechnicianFilter) filters.technician_id = parseInt(selectedTechnicianFilter);

      const [bookingsResp, catchUpResp] = await Promise.all([
        bookingAPI.getAllBookings(filters),
        bookingAPI.getCatchUpDays({ date_from: dateFrom, date_to: dateTo })
      ]);
      setBookings(bookingsResp.data.bookings || []);
      setCatchUpDays(catchUpResp.data.catch_up_days || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(getWeekStart(newDate));
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
    setCurrentMonth(new Date());
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getBookingsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredBookings.filter(b => b.booking_date === dateStr);
  };

  const isCatchUpDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return catchUpDays.some(c => c.catch_up_date === dateStr);
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await bookingAPI.updateBookingStatus(bookingId, newStatus);
      fetchData();
      // Update the selected booking status without closing the modal
      setSelectedBooking(prev => prev ? {...prev, status: newStatus} : null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update booking status');
    }
  };

  const startEditingBooking = (booking) => {
    setEditingBooking(booking);
    // Don't allow editing bike - that's company department's responsibility
    setEditTemplateId(booking.service_template_id?.toString() || '');
    // Initialize time editing fields
    setEditBookingDate(booking.booking_date || '');
    setEditStartTime(booking.start_time?.substring(0, 5) || '');
    setEditEndTime(booking.end_time?.substring(0, 5) || '');
  };

  const saveBookingChanges = async (autoComplete = false) => {
    if (!editingBooking) return;
    
    setSavingBooking(true);
    try {
      const updateData = {
        // Don't allow editing bike_id - that's company department's responsibility
        service_template_id: editTemplateId ? parseInt(editTemplateId) : null,
        auto_complete: autoComplete,
        // Include time changes
        booking_date: editBookingDate || null,
        start_time: editStartTime || null,
        end_time: editEndTime || null
      };
      
      await bookingAPI.updateBooking(editingBooking.id, updateData);
      fetchData();
      setEditingBooking(null);
      setSelectedBooking(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update booking');
    } finally {
      setSavingBooking(false);
    }
  };

  const weekDays = getWeekDays(currentWeekStart);

  const timeSlots = [];
  for (let hour = 8; hour <= 20; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }
  const CALENDAR_START_HOUR = 8;
  const CALENDAR_END_HOUR = 21;
  const SLOT_HEIGHT_PX = 60;
  const totalHours = CALENDAR_END_HOUR - CALENDAR_START_HOUR;
  const dayColumnHeight = totalHours * SLOT_HEIGHT_PX;

  const timeToMinutes = (timeStr) => {
    const s = String(timeStr || '00:00').trim();
    const match = s.match(/^(\d{1,2}):(\d{1,2})/);
    if (!match) return 0;
    const h = Math.min(23, parseInt(match[1], 10) || 0);
    const m = Math.min(59, parseInt(match[2], 10) || 0);
    return h * 60 + m;
  };
  const bookingToPosition = (booking) => {
    const startMins = timeToMinutes(booking.start_time);
    const endMins = timeToMinutes(booking.end_time);
    const baseMins = CALENDAR_START_HOUR * 60;
    const top = Math.max(0, (startMins - baseMins) / 60 * SLOT_HEIGHT_PX);
    const bottom = Math.min(dayColumnHeight, (endMins - baseMins) / 60 * SLOT_HEIGHT_PX);
    const height = Math.max(20, bottom - top);
    return { top, height };
  };

  const desktopCalendarGridTemplate = '80px repeat(7, minmax(88px, 1fr))';
  const desktopCalendarMinWidth = '720px';

  const filteredBookings = bookings.filter(b => {
    if (statusFilter && b.status !== statusFilter) return false;
    // Include unassigned (technician_id null) when tech filter active - Square-synced bookings appear
    if (selectedTechnicianFilter && b.technician_id != null && b.technician_id !== parseInt(selectedTechnicianFilter)) return false;
    if (bikeFilter && b.bike_id !== bikeFilter) return false;
    return true;
  });
  const activeTechnicians = technicians.filter(t => t.is_active);

  const getBookingPrimaryLabel = (booking) => {
    if (booking.square_booking_id) {
      const name = booking.customer_name || (booking.user ? `${booking.user.firstname || ''} ${booking.user.surname || ''}`.trim() : '') || 'Customer';
      return name;
    }
    return booking.service_template?.name || 'Service';
  };

  const getBookingSecondaryLabel = (booking) => {
    if (booking.square_booking_id) return 'Square sync';
    return null;
  };

  // Catch-up day functions
  const fetchPendingServices = async () => {
    try {
      setLoadingPendingServices(true);
      // Get all services that don't have a booking date or are marked as pending
      const response = await adminAPI.getAllServices();
      const services = response.data.services || [];
      // Filter for services that might need follow-up or are recent
      const recent = services.filter(s => {
        const serviceDate = new Date(s.service_date);
        const daysDiff = (new Date() - serviceDate) / (1000 * 60 * 60 * 24);
        return daysDiff < 30; // Services from last 30 days
      });
      setPendingServices(recent.slice(0, 20)); // Limit to 20
    } catch (err) {
      console.error('Error fetching services for catch-up:', err);
    } finally {
      setLoadingPendingServices(false);
    }
  };

  const togglePendingServiceSelection = (serviceId) => {
    setSelectedPendingServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const createCatchUpBookings = async () => {
    setCreatingCatchUp(true);
    try {
      await bookingAPI.createCatchUpDay({
        catch_up_date: catchUpDate,
        notes: selectedPendingServices.length > 0
          ? `${selectedPendingServices.length} pending service(s) for follow-up`
          : null
      });
      setShowCatchUpModal(false);
      setSelectedPendingServices([]);
      setCatchUpDate(new Date().toISOString().split('T')[0]);
      // Refresh calendar so catch-up days appear
      fetchData();
      alert(`Catch-up day created for ${catchUpDate}. No bike required - reserved for admin work, paperwork, or shop maintenance.`);
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while creating the catch-up day');
    } finally {
      setCreatingCatchUp(false);
    }
  };

  const openCatchUpModal = () => {
    setShowCatchUpModal(true);
    fetchPendingServices();
  };

  const tier = (company?.subscription_tier || '').toLowerCase();
  const showLockedOverlay = company && tier !== 'enterprise' && tier !== 'lifetime';

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle="Bookings Calendar"
      pageSubtitle={showLockedOverlay ? undefined : `${formatDate(currentWeekStart)} - ${formatDate(weekDays[6])}`}
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
    >
      {showLockedOverlay ? (
        <EnterpriseLockedOverlay
          title="Booking Calendar"
          description="Manage appointments, view weekly or monthly schedule, and sync with Square."
          darkMode={darkMode}
        />
      ) : loading ? (
        <div className="loading">Loading calendar...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
      <>
      {/* Filters and View Toggle */}
      <div style={{
        background: darkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
        border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.1)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
              Date range
            </label>
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="form-input"
            >
              <option value="week">Weekly view</option>
              <option value="month">Monthly view</option>
            </select>
          </div>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
              Status filter
            </label>
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setBikeFilter(null); // Clear bike filter when status filter changes
              }}
              className="form-input"
            >
              <option value="">All statuses</option>
              {Object.keys(STATUS_COLORS).map(status => (
                <option key={status} value={status}>{status.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          {bikeFilter && (
            <div style={{ flex: '1', minWidth: '150px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                Bike filter (active)
              </label>
              <button
                onClick={() => setBikeFilter(null)}
                className="btn btn-secondary"
                style={{ width: '100%', padding: '10px' }}
              >
                Clear bike filter
              </button>
            </div>
          )}
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
              Technician filter
            </label>
            <select 
              value={selectedTechnicianFilter} 
              onChange={(e) => setSelectedTechnicianFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All technicians</option>
              {technicians.map(tech => (
                <option key={tech.id} value={tech.id}>{tech.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1.5', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
              View
            </label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setViewMode('calendar')}
                style={{ 
                  flex: 1,
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: viewMode === 'calendar' 
                    ? '#4A8C18' 
                    : (darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'),
                  color: viewMode === 'calendar' 
                    ? '#ffffff' 
                    : (darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'),
                  boxShadow: viewMode === 'calendar' ? '0 4px 12px rgba(74, 140, 24, 0.3)' : 'none'
                }}
              >
                <Icon name="calendar" color={viewMode === 'calendar' ? "primary" : "secondary"} darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                Calendar
              </button>
              <button
                onClick={() => setViewMode('technician')}
                style={{ 
                  flex: 1,
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: viewMode === 'technician' 
                    ? '#4A8C18' 
                    : (darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'),
                  color: viewMode === 'technician' 
                    ? '#ffffff' 
                    : (darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'),
                  boxShadow: viewMode === 'technician' ? '0 4px 12px rgba(74, 140, 24, 0.3)' : 'none'
                }}
              >
                <Icon name="technician" color={viewMode === 'technician' ? "primary" : "secondary"} darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                By technician
              </button>
              <button
                onClick={() => setViewMode('table')}
                style={{ 
                  flex: 1,
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: viewMode === 'table' 
                    ? '#4A8C18' 
                    : (darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'),
                  color: viewMode === 'table' 
                    ? '#ffffff' 
                    : (darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'),
                  boxShadow: viewMode === 'table' ? '0 4px 12px rgba(74, 140, 24, 0.3)' : 'none'
                }}
              >
                <Icon name="analytics" color={viewMode === 'table' ? "primary" : "secondary"} darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                Table
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button 
            onClick={() => dateRange === 'week' ? navigateWeek(-1) : navigateMonth(-1)} 
            style={{
              padding: '12px 20px',
              border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.15)',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
            }}
          >
            {'<'} {dateRange === 'week' ? 'Previous week' : 'Previous month'}
          </button>
          <button 
            onClick={goToToday} 
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: '#4A8C18',
              color: '#ffffff',
              boxShadow: 'var(--shadow-glow-strong)'
            }}
          >
            Today
          </button>
          <button 
            onClick={openCatchUpModal}
            style={{
              padding: '12px 20px',
              border: darkMode ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(245, 158, 11, 0.4)',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: darkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
              color: '#f59e0b'
            }}
            title="Catch-up day: Reserve for admin work, no bike required"
          >
            <Icon name="service" color="warning" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Catch-up day
          </button>
          <button 
            onClick={() => dateRange === 'week' ? navigateWeek(1) : navigateMonth(1)} 
            style={{
              padding: '12px 20px',
              border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.15)',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
            }}
          >
            {dateRange === 'week' ? 'Next week' : 'Next month'} {'>'}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        padding: '12px 16px',
        background: darkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
        borderRadius: '12px',
        border: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.1)',
        color: 'rgba(255, 0, 0, 1)'
      }}>
        {Object.entries(STATUS_COLORS).map(([status, colors]) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '4px',
              background: darkMode ? colors.bg : colors.bgLight,
              border: `2px solid ${colors.border}`
            }} />
            <span style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', textTransform: 'capitalize' }}>
              {status.replace('_', ' ')}
            </span>
          </div>
        ))}
      </div>

      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}

      {loading ? (
        <div className="loading">Loading calendar...</div>
      ) : viewMode === 'technician' ? (
        /* Technician View - Shows bookings per technician in columns */
        <div style={{
          background: darkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
          border: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          {/* Current date display */}
          <div style={{
            padding: '16px 20px',
            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: darkMode ? 'white' : '#1a1a1a' }}>
              <Icon name="calendar" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              {dateRange === 'week' 
                ? `${formatDate(currentWeekStart)} - ${formatDate(weekDays[6])}`
                : currentMonth.toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })
              }
            </h3>
          </div>
          
          {/* Unified header + grid scroller to keep columns perfectly aligned */}
          <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto', minWidth: `${100 + activeTechnicians.length * 180}px` }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `80px repeat(${activeTechnicians.length || 1}, 1fr)`,
              borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.06)',
              minWidth: `${100 + activeTechnicians.length * 180}px`,
              position: 'sticky',
              top: 0,
              zIndex: 5
            }}>
              <div style={{
                padding: '16px 12px',
                background: darkMode ? '#101526' : '#f3f4f6',
                fontWeight: '600',
                textAlign: 'center',
                color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                fontSize: '12px',
                textTransform: 'uppercase'
              }}>
                Time
              </div>
              {activeTechnicians.map((tech) => (
                <div
                  key={tech.id}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    background: darkMode ? '#101526' : 'rgba(61, 90, 32, 0.05)',
                    borderLeft: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: '600', color: darkMode ? 'white' : '#1a1a1a' }}>
                    <Icon name="technician" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    {tech.name}
                  </div>
                  {tech.specializations && (
                    <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginTop: '2px' }}>
                      {tech.specializations}
                    </div>
                  )}
                  <div style={{
                    fontSize: '11px',
                    color: darkMode ? '#4A8C18' : '#1A2811',
                    marginTop: '4px',
                    fontWeight: '600'
                  }}>
                    {filteredBookings.filter(b => b.technician_id === tech.id && b.status !== 'cancelled').length} bookings
                  </div>
                </div>
              ))}
              {activeTechnicians.length === 0 && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
                }}>
                  No active technician
                </div>
              )}
            </div>

            {timeSlots.map((timeSlot) => (
              <div
                key={timeSlot}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `80px repeat(${activeTechnicians.length || 1}, 1fr)`,
                  borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.03)' : '1px solid rgba(0, 0, 0, 0.03)',
                  minHeight: '70px'
                }}
              >
                <div style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                  textAlign: 'center',
                  background: darkMode ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
                  borderRight: 'none'
                }}>
                  {timeSlot}
                </div>
                {activeTechnicians.map((tech) => {
                  // Get bookings for this technician in this time slot (across all days in current range)
                  const techSlotBookings = bookings.filter(b => {
                    if (b.technician_id !== tech.id) return false;
                    if (b.status === 'cancelled') return false;
                    const bookingHour = parseInt(b.start_time?.substring(0, 2) || '0');
                    const slotHour = parseInt(timeSlot.substring(0, 2));
                    return bookingHour === slotHour;
                  });

                  return (
                    <div
                      key={tech.id}
                      onClick={() => {
                        if (techSlotBookings.length === 0) {
                          // Get today or first day of week
                          const defaultDate = dateRange === 'week' 
                            ? currentWeekStart.toISOString().split('T')[0]
                            : new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString().split('T')[0];
                          setNewBookingDateTime({ date: defaultDate, time: timeSlot });
                          setNewBookingFormData(prev => ({
                            ...prev, 
                            start_time: timeSlot,
                            technician_id: tech.id.toString()
                          }));
                          setShowAddBookingModal(true);
                        }
                      }}
                      style={{
                        padding: '4px',
                        borderLeft: darkMode ? '1px solid rgba(255,255,255,0.03)' : '1px solid rgba(0,0,0,0.03)',
                        background: 'transparent',
                        cursor: techSlotBookings.length === 0 ? 'pointer' : 'default',
                        transition: 'background 0.15s',
                        minHeight: '60px'
                      }}
                      onMouseEnter={(e) => {
                        if (techSlotBookings.length === 0) {
                          e.currentTarget.style.background = darkMode ? 'rgba(61, 90, 32, 0.1)' : 'rgba(61, 90, 32, 0.08)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      {techSlotBookings.map((booking) => {
                        const statusColors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                        return (
                          <div
                            key={booking.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking(booking);
                            }}
                            style={{
                              padding: '6px 8px',
                              marginBottom: '4px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              background: darkMode ? statusColors.bg : statusColors.bgLight,
                              borderLeft: `3px solid ${statusColors.border}`,
                              color: darkMode ? statusColors.text : statusColors.textLight,
                              transition: 'transform 0.1s'
                            }}
                          >
                            <div style={{ fontWeight: '600', fontSize: '10px', marginBottom: '2px' }}>
                              <Icon name="calendar" color="secondary" darkMode={darkMode} size={10} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {new Date(booking.booking_date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                            </div>
                            <div style={{ fontWeight: '600' }}>
                              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </div>
                            <div style={{ opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {getBookingPrimaryLabel(booking)}
                            </div>
                            {getBookingSecondaryLabel(booking) && (
                              <div style={{ opacity: 0.6, fontSize: '9px' }}>Square sync</div>
                            )}
                          {booking.bike && (
                            <div style={{ opacity: 0.6, fontSize: '10px', marginTop: '2px' }}>
                              <Icon name="bike" color="secondary" darkMode={darkMode} size={10} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {booking.square_booking_id && isSquarePlaceholderVin(booking.bike?.vin) ? 'Square sync' : `${booking.bike.brand} ${booking.bike.model}`}
                            </div>
                          )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Unassigned bookings section */}
          {bookings.filter(b => !b.technician_id && b.status !== 'cancelled').length > 0 && (
            <div style={{
              padding: '16px 20px',
              borderTop: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              background: darkMode ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.05)'
            }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '14px', 
                color: darkMode ? '#fbbf24' : '#d97706',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon name="warning" color="warning" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Unassigned bookings ({bookings.filter(b => !b.technician_id && b.status !== 'cancelled').length})
              </h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {bookings.filter(b => !b.technician_id && b.status !== 'cancelled').map(booking => (
                  <div
                    key={booking.id}
                    onClick={() => setSelectedBooking(booking)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      background: darkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: darkMode ? '#fbbf24' : '#d97706'
                    }}
                  >
                    <div style={{ fontWeight: '600' }}>
                      {new Date(booking.booking_date).toLocaleDateString('en-GB')} {formatTime(booking.start_time)}
                    </div>
                    <div style={{ opacity: 0.8 }}>
                      {getBookingPrimaryLabel(booking)}
                    </div>
                    {getBookingSecondaryLabel(booking) && (
                      <div style={{ opacity: 0.6, fontSize: '10px' }}>Square sync</div>
                    )}
                    {!getBookingSecondaryLabel(booking) && (booking.customer_name || booking.user) && (
                      <div style={{ opacity: 0.7, fontSize: '11px' }}>
                        {booking.customer_name || (booking.user ? `${booking.user.firstname || ''} ${booking.user.surname || ''}`.trim() : '')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : viewMode === 'table' ? (
        /* Table View */
        <div style={{
          background: darkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
          border: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ 
                  background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', 
                  borderBottom: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' 
                }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>Time</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>Service</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>Bike</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>Customer</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Icon name="technician" color="secondary" darkMode={darkMode} size={13} />
                    Technician
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                      No bookings
                    </td>
                  </tr>
                ) : (
                  bookings.map(booking => {
                    const statusColors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                    return (
                      <tr 
                        key={booking.id}
                        style={{ 
                          borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={() => {
                          setSelectedBooking(booking);
                          // When status filter is active and in monthly view, filter by bike instead of user
                          if (statusFilter && dateRange === 'month' && booking.bike_id) {
                            setBikeFilter(booking.bike_id);
                          }
                        }}
                      >
                        <td style={{ padding: '12px 16px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                          {new Date(booking.booking_date).toLocaleDateString('en-GB')}
                        </td>
                        <td style={{ padding: '12px 16px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                          {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                        </td>
                        <td style={{ padding: '12px 16px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                          {getBookingPrimaryLabel(booking)}{getBookingSecondaryLabel(booking) ? ' | Square sync' : ''}
                        </td>
                        <td style={{ padding: '12px 16px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                          {booking.bike
                            ? (booking.square_booking_id && isSquarePlaceholderVin(booking.bike?.vin) ? 'Square sync (no bike)' : `${booking.bike.brand} ${booking.bike.model}`)
                            : '-'}
                        </td>
                        <td style={{ padding: '12px 16px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                          {booking.customer_name || (booking.user ? `${booking.user.firstname || ''} ${booking.user.surname || ''}`.trim() : null) || '-'}
                        </td>
                        <td style={{ padding: '12px 16px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                          {booking.technician ? (
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              background: darkMode ? 'rgba(61, 90, 32, 0.15)' : 'rgba(61, 90, 32, 0.1)',
                              color: darkMode ? '#4A8C18' : '#1A2811',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              <Icon name="technician" color="secondary" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {booking.technician.name}
                            </span>
                          ) : (
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              background: darkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                              color: darkMode ? '#fbbf24' : '#d97706',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              <Icon name="warning" color="warning" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              Nincs
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: darkMode ? statusColors.bg : statusColors.bgLight,
                            color: darkMode ? statusColors.text : statusColors.textLight,
                            textTransform: 'capitalize'
                          }}>
                            {booking.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking(booking);
                            }}
                            className="btn btn-sm btn-secondary"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : dateRange === 'month' ? (
        /* Monthly Calendar View */
        <div style={{
          background: darkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
          border: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          padding: '20px'
        }}>
          {/* Month header */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '20px', 
              fontWeight: '600', 
              color: darkMode ? 'white' : '#1a1a1a' 
            }}>
              {currentMonth.toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}
            </h3>
          </div>
          
          {/* Weekday headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '8px'
          }}>
            {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d, idx) => (
              <div key={idx} style={{
                padding: '8px',
                textAlign: 'center',
                fontSize: '12px',
                fontWeight: '600',
                color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
              }}>
                {d}
              </div>
            ))}
          </div>
          
          {/* Calendar days */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px'
          }}>
            {(() => {
              const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
              const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
              const startOffset = (firstDay.getDay() + 6) % 7; // Monday = 0
              const cells = [];
              
              // Empty cells before first day
              for (let i = 0; i < startOffset; i++) {
                cells.push(<div key={`empty-${i}`} style={{ minHeight: '80px' }} />);
              }
              
              // Day cells
              for (let day = 1; day <= lastDay.getDate(); day++) {
                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                const dateStr = date.toISOString().split('T')[0];
                const dayBookings = filteredBookings.filter(b => b.booking_date === dateStr);
                const isTodayDate = isToday(date);
                const hasCatchUp = isCatchUpDay(date);
                
                cells.push(
                  <div
                    key={day}
                    onClick={() => {
                      setNewBookingDateTime({ date: dateStr, time: '09:00' });
                      setNewBookingFormData(prev => ({...prev, start_time: '09:00'}));
                      setShowAddBookingModal(true);
                    }}
                    style={{
                      minHeight: '80px',
                      padding: '8px',
                      borderRadius: '8px',
                      background: isTodayDate 
                        ? 'rgba(61, 90, 32, 0.15)' 
                        : darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                      border: isTodayDate 
                        ? '2px solid #3d5a20' 
                        : darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                      cursor: 'pointer',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isTodayDate) {
                        e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isTodayDate) {
                        e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';
                      }
                    }}
                  >
                    <div style={{
                      fontSize: '14px',
                      fontWeight: isTodayDate ? '700' : '500',
                      color: isTodayDate ? '#3d5a20' : darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                      marginBottom: '4px'
                    }}>
                      {day}
                    </div>
                    {hasCatchUp && (
                      <div style={{
                        padding: '2px 6px',
                        marginBottom: '4px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600',
                        background: darkMode ? 'rgba(59, 130, 246, 0.25)' : 'rgba(59, 130, 246, 0.2)',
                        color: darkMode ? '#93c5fd' : '#1e40af',
                        cursor: 'default'
                      }}>
                        Catch-up day
                      </div>
                    )}
                    {dayBookings.slice(0, 3).map((booking, idx) => (
                      <div
                        key={booking.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBooking(booking);
                        }}
                        style={{
                          padding: '2px 6px',
                          marginBottom: '2px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '500',
                          background: darkMode ? STATUS_COLORS[booking.status]?.bg : STATUS_COLORS[booking.status]?.bgLight,
                          color: darkMode ? STATUS_COLORS[booking.status]?.text : STATUS_COLORS[booking.status]?.textLight,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer'
                        }}
                      >
                        {formatTime(booking.start_time)} {getBookingPrimaryLabel(booking).substring(0, 8)}{getBookingSecondaryLabel(booking) ? ' (SQ)' : ''}
                      </div>
                    ))}
                    {dayBookings.length > 3 && (
                      <div style={{
                        fontSize: '10px',
                        color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
                      }}>
                        +{dayBookings.length - 3} more
                      </div>
                    )}
                  </div>
                );
              }
              
              return cells;
            })()}
          </div>
        </div>
      ) : isMobileView ? (
        /* Mobile Day View */
        <div 
          style={{
            background: darkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            overflow: 'hidden'
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Mobile Day Navigation Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
            borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.06)'
          }}>
            <button
              onClick={goToMobilePrevDay}
              style={{
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 16px',
                cursor: 'pointer',
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                fontSize: '18px'
              }}
            >
              {'<'}
            </button>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '13px', 
                color: isToday(mobileSelectedDay) ? '#4A8C18' : darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', 
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {mobileSelectedDay.toLocaleDateString('en-GB', { weekday: 'long' })}
              </div>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: isToday(mobileSelectedDay) ? '#4A8C18' : darkMode ? 'white' : '#1a1a1a'
              }}>
                {mobileSelectedDay.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </div>
              {isToday(mobileSelectedDay) && (
                <div style={{ 
                  fontSize: '11px', 
                  color: '#4A8C18',
                  fontWeight: '600',
                  marginTop: '4px'
                }}>
                  TODAY
                </div>
              )}
            </div>

            <button
              onClick={goToMobileNextDay}
              style={{
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 16px',
                cursor: 'pointer',
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                fontSize: '18px'
              }}
            >
              {'>'}
            </button>
          </div>

          {/* Swipe hint */}
          <div style={{
            textAlign: 'center',
            padding: '8px',
            fontSize: '11px',
            color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            background: darkMode ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)'
          }}>
            {'<-'} Swipe to navigate {'->'}
          </div>

          {/* Mobile Time Slots - bookings that START in this hour, row height scales with duration */}
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {timeSlots.map((timeSlot, slotIdx) => {
              const dayBookings = getBookingsForDay(mobileSelectedDay);
              const slotBookings = dayBookings.filter(b => {
                const bookingHour = parseInt(b.start_time?.substring(0, 2) || '0');
                const slotHour = parseInt(timeSlot.substring(0, 2));
                return bookingHour === slotHour;
              });
              const maxSpanMins = slotBookings.length
                ? Math.max(...slotBookings.map(b => timeToMinutes(b.end_time) - timeToMinutes(b.start_time)))
                : 60;
              const rowHeight = Math.max(60, Math.min(240, (maxSpanMins / 60) * SLOT_HEIGHT_PX));

              return (
                <div
                  key={timeSlot}
                  style={{
                    display: 'flex',
                    borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.03)' : '1px solid rgba(0, 0, 0, 0.03)',
                    minHeight: `${rowHeight}px`
                  }}
                >
                  <div style={{
                    width: '60px',
                    flexShrink: 0,
                    padding: '12px 8px',
                    fontSize: '12px',
                    color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                    textAlign: 'center',
                    background: darkMode ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
                    borderRight: 'none'
                  }}>
                    {timeSlot}
                  </div>
                  <div 
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: slotBookings.length === 0 ? 'transparent' : 'transparent',
                      cursor: slotBookings.length === 0 ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (slotBookings.length === 0) {
                        const dateStr = mobileSelectedDay.toISOString().split('T')[0];
                        setNewBookingDateTime({ date: dateStr, time: timeSlot });
                        setNewBookingFormData(prev => ({...prev, start_time: timeSlot}));
                        setShowAddBookingModal(true);
                      }
                    }}
                  >
                    {slotBookings.map((booking, bIdx) => {
                      const statusColors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                      return (
                        <div
                          key={booking.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBooking(booking);
                          }}
                          style={{
                            padding: '12px',
                            marginBottom: bIdx < slotBookings.length - 1 ? '8px' : 0,
                            borderRadius: '10px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            background: darkMode ? statusColors.bg : statusColors.bgLight,
                            borderLeft: `4px solid ${statusColors.border}`,
                            color: darkMode ? statusColors.text : statusColors.textLight
                          }}
                        >
                          <div style={{ fontWeight: '700', marginBottom: '4px' }}>
                            {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                          </div>
                          <div style={{ opacity: 0.9, marginBottom: '4px' }}>
                            {getBookingPrimaryLabel(booking)}
                          </div>
                          {getBookingSecondaryLabel(booking) && (
                            <div style={{ opacity: 0.7, fontSize: '11px', marginBottom: '4px' }}>Square sync</div>
                          )}
                          {booking.bike && (
                            <div style={{ opacity: 0.7, fontSize: '12px', marginBottom: '2px' }}>
                              {booking.square_booking_id && isSquarePlaceholderVin(booking.bike?.vin) ? 'Square sync' : `${booking.bike.brand} ${booking.bike.model}`}
                            </div>
                          )}
                          {(booking.customer_name || booking.user) && (
                            <div style={{ opacity: 0.7, fontSize: '12px', marginBottom: '2px' }}>
                              <Icon name="profile" color="secondary" darkMode={darkMode} size={11} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {booking.customer_name || (booking.user ? `${booking.user.firstname || ''} ${booking.user.surname || ''}`.trim() : '')}
                            </div>
                          )}
                          {booking.technician && (
                            <div style={{ opacity: 0.6, fontSize: '11px', marginTop: '4px' }}>
                              <Icon name="technician" color="secondary" darkMode={darkMode} size={10} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {booking.technician.name}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {slotBookings.length === 0 && (
                      <div style={{
                        height: '100%',
                        minHeight: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        fontSize: '12px'
                      }}>
                        Tap to add booking
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Weekly Calendar View (Desktop) */
        <div style={{
          background: darkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
          border: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          overflowX: 'auto'
        }}>
          {/* Unified header + body scroller to keep columns perfectly aligned */}
          <div style={{ maxHeight: '600px', overflowY: 'auto', minWidth: desktopCalendarMinWidth }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: desktopCalendarGridTemplate,
              borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.06)',
              minWidth: desktopCalendarMinWidth,
              position: 'sticky',
              top: 0,
              zIndex: 5
            }}>
              <div style={{
                padding: '16px 12px',
                background: darkMode ? '#101526' : '#f3f4f6',
                fontWeight: '600',
                textAlign: 'center',
                color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                fontSize: '12px',
                textTransform: 'uppercase'
              }}>
                Time
              </div>
              {weekDays.map((day, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    background: isToday(day)
                      ? 'rgba(61, 90, 32, 0.15)'
                      : darkMode ? '#101526' : '#f3f4f6',
                    borderLeft: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
                  }}
                >
                  <div style={{ fontSize: '11px', color: isToday(day) ? '#3d5a20' : darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', textTransform: 'uppercase' }}>
                    {day.toLocaleDateString('en-GB', { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: isToday(day) ? (darkMode ? 'white' : '#1a1a1a') : darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', marginTop: '4px' }}>
                    {day.getDate()}
                  </div>
                  {isCatchUpDay(day) && (
                    <div style={{
                      fontSize: '9px',
                      fontWeight: '600',
                      color: darkMode ? '#93c5fd' : '#1e40af',
                      marginTop: '4px'
                    }}>
                      Catch-up
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Time-scaled body: time labels + day columns with absolutely positioned bookings */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: desktopCalendarGridTemplate,
              minWidth: desktopCalendarMinWidth,
              minHeight: dayColumnHeight
            }}>
              {/* Time labels column */}
              <div style={{
                position: 'relative',
                height: dayColumnHeight,
                borderRight: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                background: darkMode ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)'
              }}>
                {timeSlots.map((timeSlot, i) => (
                  <div
                    key={timeSlot}
                    style={{
                      position: 'absolute',
                      top: i * SLOT_HEIGHT_PX,
                      left: 0,
                      right: 0,
                      padding: '4px 8px',
                      fontSize: '11px',
                      color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                      textAlign: 'right',
                      borderTop: i > 0 ? (darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.06)') : 'none'
                    }}
                  >
                    {timeSlot}
                  </div>
                ))}
              </div>
              {/* Day columns with time-scaled booking blocks */}
              {weekDays.map((day, dayIdx) => {
                const dayBookings = getBookingsForDay(day);
                return (
                  <div
                    key={dayIdx}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const y = e.clientY - rect.top;
                      const slotIndex = Math.floor(y / SLOT_HEIGHT_PX);
                      const hour = Math.min(20, Math.max(8, CALENDAR_START_HOUR + slotIndex));
                      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                      const dateStr = day.toISOString().split('T')[0];
                      setNewBookingDateTime({ date: dateStr, time: timeStr });
                      setNewBookingFormData(prev => ({...prev, start_time: timeStr}));
                      setShowAddBookingModal(true);
                    }}
                    style={{
                      position: 'relative',
                      height: dayColumnHeight,
                      background: isToday(day) ? 'rgba(61, 90, 32, 0.05)' : 'transparent',
                      borderLeft: darkMode ? '1px solid rgba(255,255,255,0.03)' : '1px solid rgba(0,0,0,0.03)',
                      cursor: 'pointer'
                    }}
                  >
                    {/* Hour grid lines */}
                    {timeSlots.map((_, i) => (
                      <div
                        key={i}
                        style={{
                          position: 'absolute',
                          top: i * SLOT_HEIGHT_PX,
                          left: 0,
                          right: 0,
                          height: 1,
                          borderTop: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.06)',
                          pointerEvents: 'none'
                        }}
                      />
                    ))}
                    {/* Booking blocks - absolutely positioned, span across hours (sorted by start_time) */}
                    {[...dayBookings]
                      .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time))
                      .map((booking) => {
                        const { top, height } = bookingToPosition(booking);
                        const statusColors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                        const blockHeight = Math.max(24, height - 2);
                        const isTinyBlock = blockHeight < 34;
                        const isCompactBlock = blockHeight >= 34 && blockHeight < 52;
                        const showSecondaryLine = blockHeight >= 34;
                        const showTechnicianLine = blockHeight >= 70 && booking.technician;

                        return (
                          <div
                            key={booking.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking(booking);
                            }}
                            style={{
                              position: 'absolute',
                              top: `${top}px`,
                              left: '4px',
                              right: '4px',
                              height: `${blockHeight}px`,
                              padding: isTinyBlock ? '2px 5px' : '4px 6px',
                              borderRadius: '6px',
                              fontSize: isTinyBlock ? '10px' : '11px',
                              cursor: 'pointer',
                              background: darkMode ? statusColors.bg : statusColors.bgLight,
                              borderLeft: `3px solid ${statusColors.border}`,
                              color: darkMode ? statusColors.text : statusColors.textLight,
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              gap: isTinyBlock ? 0 : '1px'
                            }}
                          >
                            <div style={{ fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: isTinyBlock ? 1.1 : 1.2 }}>
                              {isTinyBlock
                                ? `${formatTime(booking.start_time)} ${getBookingPrimaryLabel(booking)}`
                                : getBookingPrimaryLabel(booking)}
                            </div>
                            {showSecondaryLine && (
                              <div style={{ opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: isCompactBlock ? '9px' : '10px', lineHeight: 1.2 }}>
                                {formatTime(booking.start_time)}-{formatTime(booking.end_time)}
                                {getBookingSecondaryLabel(booking) && ' | Square sync'}
                              </div>
                            )}
                            {showTechnicianLine && (
                              <div style={{ opacity: 0.7, fontSize: '9px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <Icon name="technician" color="secondary" darkMode={darkMode} size={9} style={{ flexShrink: 0 }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{booking.technician.name}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '12px'
      }}>
        {Object.entries(STATUS_COLORS).map(([status, colors]) => {
          const count = filteredBookings.filter(b => b.status === status).length;
          return (
            <div
              key={status}
              style={{
                padding: '16px',
                borderRadius: '12px',
                background: darkMode ? colors.bg : colors.bgLight,
                border: `1px solid ${colors.border}${darkMode ? '30' : '50'}`,
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? colors.text : colors.textLight }}>
                {count}
              </div>
              <div style={{ fontSize: '12px', textTransform: 'capitalize', color: darkMode ? colors.text : colors.textLight, opacity: 0.8 }}>
                {status.replace('_', ' ')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => setSelectedBooking(null)}
        >
          <div
            style={{
              background: darkMode ? '#0a0a1a' : '#ffffff',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: darkMode ? 'white' : '#1a1a1a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="details" color="primary" darkMode={darkMode} size={20} />
                Booking Details
              </h2>
              <button
                onClick={() => setSelectedBooking(null)}
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                }}
              >
                x
              </button>
            </div>

            <div style={{
              padding: '12px 16px',
              borderRadius: '10px',
              background: darkMode ? STATUS_COLORS[selectedBooking.status]?.bg : STATUS_COLORS[selectedBooking.status]?.bgLight,
              border: `1px solid ${STATUS_COLORS[selectedBooking.status]?.border}40`,
              marginBottom: '20px'
            }}>
              <span style={{ color: darkMode ? STATUS_COLORS[selectedBooking.status]?.text : STATUS_COLORS[selectedBooking.status]?.textLight, fontWeight: '600' }}>
                Status: {selectedBooking.status?.replace('_', ' ').toUpperCase()}
              </span>
            </div>

            <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                <Icon name="calendar" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                <span>{selectedBooking.booking_date}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                <Icon name="schedule" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                <span>{formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                <Icon name="service" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                <span>{getBookingPrimaryLabel(selectedBooking)}{getBookingSecondaryLabel(selectedBooking) ? ' | Square sync' : ''}</span>
              </div>
              {selectedBooking.service?.description && (
                <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.75)' : '#374151' }}>
                  <Icon name="write" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                  <span>{selectedBooking.service.description}</span>
                </div>
              )}
              {selectedBooking.service?.latest_comment?.comment_text && (
                <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.75)' : '#374151', fontStyle: 'italic' }}>
                  <Icon name="chat" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                  <span>{selectedBooking.service.latest_comment.comment_text}</span>
                </div>
              )}
              {selectedBooking.bike && (
                <>
                  <div 
                    style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      color: darkMode ? '#84cc16' : '#15803d',
                      cursor: 'pointer',
                      padding: '8px 12px',
                      margin: '-8px -12px',
                      borderRadius: '8px',
                      transition: 'background 0.2s'
                    }}
                    onClick={() => setPassportBike(selectedBooking.bike)}
                    onMouseOver={(e) => e.currentTarget.style.background = darkMode ? 'rgba(132, 204, 22, 0.15)' : 'rgba(21, 128, 61, 0.1)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    title="Click to view bike details"
                  >
                    <Icon name="bike" color="brand" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                    <span style={{ fontWeight: '600' }}>
                      {selectedBooking.square_booking_id && isSquarePlaceholderVin(selectedBooking.bike?.vin)
                        ? 'Booking (no bike specified)'
                        : `${selectedBooking.bike.brand || ''} ${selectedBooking.bike.model || ''}`.trim()}
                      <span style={{ marginLeft: '8px', opacity: 0.7 }}>{'->'}</span>
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', marginLeft: '24px', fontSize: '13px' }}>
                    <Icon name="details" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle' }} />
                    <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                      {selectedBooking.square_booking_id && isSquarePlaceholderVin(selectedBooking.bike?.vin)
                        ? 'Square sync (no bike specified)'
                        : `Frame: ${selectedBooking.bike.vin}`}
                    </span>
                  </div>
                  {selectedBooking.square_booking_id && isSquarePlaceholderVin(selectedBooking.bike?.vin) && (
                    <div style={{ marginLeft: '24px', marginTop: '10px' }}>
                      <button
                        onClick={() => {
                          const params = new URLSearchParams();
                          params.set('tab', 'services');
                          params.set('action', 'add');
                          params.set('bookingId', selectedBooking.id);
                          params.set('bookingNeedsBikeSelection', '1');
                          params.set('description', selectedBooking.service_template?.name || '');
                          params.set('serviceDate', selectedBooking.booking_date);
                          params.set('serviceTime', (selectedBooking.start_time || '').substring(0, 5) || '09:00');
                          if (selectedBooking.service_template?.default_price) params.set('defaultPrice', selectedBooking.service_template.default_price);
                          navigate(`/admin/management?${params.toString()}`);
                          setSelectedBooking(null);
                        }}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: darkMode ? '1px solid rgba(96, 165, 250, 0.45)' : '1px solid rgba(37, 99, 235, 0.35)',
                          background: darkMode ? 'rgba(59, 130, 246, 0.16)' : 'rgba(59, 130, 246, 0.1)',
                          color: darkMode ? '#93c5fd' : '#1d4ed8',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                        title="Select an existing bike passport (or create a new bike) before recording service"
                      >
                        Link to Existing Bike Passport
                      </button>
                    </div>
                  )}
                </>
              )}
              {(selectedBooking.customer_name || selectedBooking.customer_email || selectedBooking.customer_phone || selectedBooking.user) && (
                <>
                  <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                    <Icon name="profile" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                    <span>{selectedBooking.customer_name || (selectedBooking.user ? `${selectedBooking.user.firstname || ''} ${selectedBooking.user.surname || ''}`.trim() : '') || '-'}</span>
                  </div>
                  {(selectedBooking.customer_email || selectedBooking.user?.email) && (
                    <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', marginLeft: '24px', fontSize: '13px' }}>
                      <a href={`mailto:${selectedBooking.customer_email || selectedBooking.user?.email}`} style={{ color: darkMode ? '#84cc16' : '#15803d', textDecoration: 'none' }}>
                        {selectedBooking.customer_email || selectedBooking.user?.email}
                      </a>
                    </div>
                  )}
                  {(selectedBooking.customer_phone || selectedBooking.user?.phone) && (
                    <div style={{ display: 'flex', gap: '8px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', marginLeft: '24px', fontSize: '13px' }}>
                      <Icon name="schedule" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle' }} />
                      <a 
                        href={`tel:${selectedBooking.customer_phone || selectedBooking.user?.phone}`}
                        style={{ color: darkMode ? '#84cc16' : '#15803d', textDecoration: 'none' }}
                      >
                        {selectedBooking.customer_phone || selectedBooking.user?.phone}
                      </a>
                    </div>
                  )}
                </>
              )}
              {/* Technician display/edit */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                alignItems: 'center',
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                padding: '8px 0'
              }}>
                <Icon name="technician" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle' }} />
                {selectedBooking.status !== 'completed' && selectedBooking.status !== 'cancelled' ? (
                  <select
                    value={selectedBooking.technician_id || ''}
                    onChange={async (e) => {
                      try {
                        await bookingAPI.updateBooking(selectedBooking.id, {
                          technician_id: e.target.value ? parseInt(e.target.value) : null
                        });
                        fetchData();
                        setSelectedBooking(prev => ({
                          ...prev,
                          technician_id: e.target.value ? parseInt(e.target.value) : null,
                          technician: technicians.find(t => t.id === parseInt(e.target.value)) || null
                        }));
                      } catch (err) {
                        setError(err.response?.data?.error || 'Failed to modify the service');
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                      background: darkMode ? '#0a0a1a' : '#ffffff',
                      color: darkMode ? 'white' : '#1a1a1a',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Not assigned</option>
                    {technicians.filter(t => t.is_active).map(tech => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span>{selectedBooking.technician?.name || 'Not assigned'}</span>
                )}
              </div>
            </div>

            {/* Edit Bike and Template Section - Only for non-completed/cancelled bookings */}
            {selectedBooking.status !== 'completed' && selectedBooking.status !== 'cancelled' ? (
              editingBooking?.id === selectedBooking.id ? (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: darkMode ? 'rgba(61, 90, 32, 0.08)' : 'rgba(61, 90, 32, 0.1)',
                  borderRadius: '12px',
                  border: darkMode ? '1px solid rgba(61, 90, 32, 0.2)' : '1px solid rgba(61, 90, 32, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', color: darkMode ? '#a3e635' : '#65a30d' }}>
                    <Icon name="edit" color="brand" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Edit Booking
                  </h4>
                  
                  {/* Date and Time Editing */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                      Date
                    </label>
                    <input
                      type="date"
                      value={editBookingDate}
                      onChange={(e) => setEditBookingDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                        background: darkMode ? '#0a0a1a' : '#ffffff',
                        color: darkMode ? 'white' : '#1a1a1a',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={editStartTime}
                        onChange={(e) => setEditStartTime(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                          background: darkMode ? '#0a0a1a' : '#ffffff',
                          color: darkMode ? 'white' : '#1a1a1a',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                        End Time
                      </label>
                      <input
                        type="time"
                        value={editEndTime}
                        onChange={(e) => setEditEndTime(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                          background: darkMode ? '#0a0a1a' : '#ffffff',
                          color: darkMode ? 'white' : '#1a1a1a',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                      Service Type
                    </label>
                    <select
                      value={editTemplateId}
                      onChange={(e) => setEditTemplateId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                        background: darkMode ? '#0a0a1a' : '#ffffff',
                        color: darkMode ? 'white' : '#1a1a1a',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">-- Select service type --</option>
                      {serviceTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.estimated_duration_minutes} min)
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => saveBookingChanges(false)}
                      disabled={savingBooking}
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                    >
                      {savingBooking ? (
                        <>
                          <Icon name="loading" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Icon name="success" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                          Save
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => saveBookingChanges(true)}
                      disabled={savingBooking || !editTemplateId}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: savingBooking || !editTemplateId ? 'not-allowed' : 'pointer',
                        background: savingBooking || !editTemplateId 
                          ? (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')
                          : 'linear-gradient(135deg, #1A2811 0%, #2a4019 100%)',
                        color: savingBooking || !editTemplateId 
                          ? (darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)')
                          : 'white',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      <Icon name="success" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                      Save + Complete
                    </button>
                    <button
                      onClick={() => setEditingBooking(null)}
                      style={{
                        padding: '10px 16px',
                        border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: 'transparent',
                        color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => startEditingBooking(selectedBooking)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    border: darkMode ? '1px solid rgba(132, 204, 22, 0.3)' : '1px solid rgba(61, 90, 32, 0.5)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    background: darkMode ? 'rgba(132, 204, 22, 0.1)' : 'rgba(61, 90, 32, 0.1)',
                    color: darkMode ? '#a3e635' : '#65a30d',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Icon name="edit" color="brand" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Edit Bike / Service
                </button>
              )
            ) : (
              <div style={{
                padding: '12px 16px',
                marginBottom: '20px',
                borderRadius: '10px',
                background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
                color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                fontSize: '13px',
                textAlign: 'center'
              }}>
                <Icon name="lock" color="warning" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                {selectedBooking.status === 'completed' ? 'Completed' : 'Cancelled'} booking - cannot be edited
              </div>
            )}

            {/* Record Service Button - navigates to AdminServices with AI receipt flow */}
            {selectedBooking.status !== 'cancelled' && !selectedBooking.service_id && (
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set('tab', 'services');
                  params.set('action', 'add');
                  params.set('bookingId', selectedBooking.id);
                  const hasRealBookingBike = Boolean(
                    selectedBooking.bike_id &&
                    !(selectedBooking.bike && isSquarePlaceholderVin(selectedBooking.bike.vin))
                  );
                  if (hasRealBookingBike) {
                    params.set('bikeId', selectedBooking.bike_id);
                  } else {
                    params.set('bookingNeedsBikeSelection', '1');
                  }
                  params.set('description', selectedBooking.service_template?.name || '');
                  params.set('serviceDate', selectedBooking.booking_date);
                  params.set('serviceTime', (selectedBooking.start_time || '').substring(0, 5) || '09:00');
                  if (selectedBooking.service_template?.default_price) params.set('defaultPrice', selectedBooking.service_template.default_price);
                  navigate(`/admin/management?${params.toString()}`);
                  setSelectedBooking(null);
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  marginBottom: '20px',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #1A2811 0%, #4A8C18 100%)',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: 'var(--shadow-glow-active)'
                }}
              >
                <Icon name="service" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
                {selectedBooking.square_booking_id && selectedBooking.bike && isSquarePlaceholderVin(selectedBooking.bike.vin)
                  ? 'Record Service (Select Bike First)'
                  : 'Record Service'}
              </button>
            )}

            {selectedBooking.service_id && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '20px',
                borderRadius: '10px',
                background: darkMode ? 'rgba(132, 204, 22, 0.1)' : 'rgba(26, 40, 17, 0.1)',
                border: darkMode ? '1px solid rgba(132, 204, 22, 0.3)' : '1px solid rgba(26, 40, 17, 0.3)',
                color: darkMode ? '#84cc16' : '#4A8C18',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon name="success" color="success" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                Service already recorded (ID: {selectedBooking.service_id})
              </div>
            )}

            {/* Status change radio buttons - hide for completed/cancelled */}
            {selectedBooking.status !== 'completed' && selectedBooking.status !== 'cancelled' && (
              <div>
                <p style={{ fontSize: '13px', marginBottom: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>Change status:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {Object.entries(STATUS_COLORS).map(([status, colors]) => (
                    <label
                      key={status}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: selectedBooking.status === status 
                          ? (darkMode ? colors.bg : colors.bgLight)
                          : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                        border: selectedBooking.status === status 
                          ? `2px solid ${colors.border}`
                          : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'),
                        transition: 'all 0.15s'
                      }}
                    >
                      <input
                        type="radio"
                        name="bookingStatus"
                        value={status}
                        checked={selectedBooking.status === status}
                        onChange={() => updateBookingStatus(selectedBooking.id, status)}
                        style={{ 
                          accentColor: colors.border,
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <span style={{
                        color: selectedBooking.status === status 
                          ? (darkMode ? colors.text : colors.textLight)
                          : (darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                        fontSize: '13px',
                        textTransform: 'capitalize',
                        fontWeight: selectedBooking.status === status ? '600' : '400'
                      }}>
                        {status.replace('_', ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Record Service Modal */}
      {showRecordServiceModal && selectedBooking && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: '24px'
          }}
          onClick={() => setShowRecordServiceModal(false)}
        >
          <div
            style={{
              background: darkMode ? '#0a0a1a' : '#ffffff',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: darkMode ? 'white' : '#1a1a1a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="service" color="primary" darkMode={darkMode} size={20} />
                Record Service
              </h2>
              <button
                onClick={() => setShowRecordServiceModal(false)}
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                }}
              >
                x
              </button>
            </div>

            {/* Bike Info from Booking */}
            {selectedBooking.bike && (
              <div style={{
                padding: '16px',
                marginBottom: '20px',
                borderRadius: '12px',
                background: darkMode ? 'rgba(61, 90, 32, 0.08)' : 'rgba(61, 90, 32, 0.1)',
                border: '1px solid rgba(61, 90, 32, 0.2)'
              }}>
                <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginBottom: '8px' }}>
                  Bike data automatically loaded:
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: darkMode ? '#d9f99d' : '#15803d' }}>
                  <Icon name="bike" color="brand" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  {selectedBooking.bike.brand} {selectedBooking.bike.model}
                </div>
                <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', marginTop: '4px', fontFamily: 'monospace' }}>
                  Frame: {selectedBooking.bike.vin}
                </div>
                {selectedBooking.user && (
                  <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', marginTop: '4px' }}>
                    <Icon name="profile" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    {selectedBooking.user.firstname} {selectedBooking.user.surname}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={async (e) => {
              e.preventDefault();
              const parsedTotalCost = Number(serviceFormData.total_cost);
              if (!Number.isFinite(parsedTotalCost) || parsedTotalCost < 0) {
                setError('Total cost must be a non-negative number.');
                return;
              }
              setSavingBooking(true);
              try {
                // Create service record
                const formData = new FormData();
                formData.append('bike_id', selectedBooking.bike_id);
                formData.append('service_date', serviceFormData.service_date);
                formData.append('description', serviceFormData.description);
                formData.append('total_cost', parsedTotalCost);
                
                const serviceResponse = await adminAPI.addService(formData);
                
                // Mark booking as completed
                const newServiceId = serviceResponse?.data?.service?.id;
                const bookingResponse = await bookingAPI.updateBookingStatus(selectedBooking.id, 'completed', newServiceId);
                
                // Refresh booking data to get the service with comments
                if (bookingResponse?.data?.booking) {
                  setSelectedBooking(bookingResponse.data.booking);
                }
                
                setShowRecordServiceModal(false);
                fetchData();
              } catch (err) {
                setError(err.response?.data?.error || 'Failed to record service');
              } finally {
                setSavingBooking(false);
              }
            }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  Service Date *
                </label>
                <input
                  type="date"
                  value={serviceFormData.service_date}
                  onChange={(e) => setServiceFormData({...serviceFormData, service_date: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? '#0a0a1a' : '#ffffff',
                    color: darkMode ? 'white' : '#1a1a1a',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  Description
                </label>
                <textarea
                  value={serviceFormData.description}
                  onChange={(e) => setServiceFormData({...serviceFormData, description: e.target.value})}
                  rows={4}
                  placeholder="Service description..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? '#0a0a1a' : '#ffffff',
                    color: darkMode ? 'white' : '#1a1a1a',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  Add Photo (optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="service-photo-upload"
                  onChange={(e) => {
                    // Photo upload will be integrated with Bucketeer later
                    if (e.target.files && e.target.files[0]) {
                      setError('Photo upload not yet implemented. Will be available after Bucketeer integration.');
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('service-photo-upload')?.click()}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                    color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Icon name="photos" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Add Photo
                </button>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  Total Cost (GBP) *
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={serviceFormData.total_cost}
                  onChange={(e) => setServiceFormData({...serviceFormData, total_cost: parseFloat(e.target.value) || 0})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? '#0a0a1a' : '#ffffff',
                    color: darkMode ? 'white' : '#1a1a1a',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  disabled={savingBooking}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: savingBooking ? 'not-allowed' : 'pointer',
                    background: 'linear-gradient(135deg, #1A2811 0%, #4A8C18 100%)',
                    color: '#ffffff',
                    fontSize: '15px',
                    fontWeight: '700',
                    opacity: savingBooking ? 0.7 : 1
                  }}
                >
                  {savingBooking ? (
                    <>
                      <Icon name="loading" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Icon name="success" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                      Record Service
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRecordServiceModal(false)}
                  style={{
                    padding: '14px 20px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    background: 'transparent',
                    color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add New Booking Modal (from calendar click) */}
      {showAddBookingModal && newBookingDateTime && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => {
            setShowAddBookingModal(false);
            setNewBookingDateTime(null);
            setNewBookingFormData({ bike_id: '', template_id: '', technician_id: '', start_time: '09:00', duration_minutes: 60, notes: '' });
          }}
        >
          <div
            style={{
              background: darkMode ? '#0a0a1a' : '#ffffff',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '550px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: darkMode ? 'white' : '#1a1a1a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="calendar" color="primary" darkMode={darkMode} size={20} />
                Add New Service
              </h2>
              <button
                onClick={() => {
                  setShowAddBookingModal(false);
                  setNewBookingDateTime(null);
                  setNewBookingFormData({ bike_id: '', template_id: '', technician_id: '', start_time: '09:00', duration_minutes: 60, notes: '' });
                }}
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                }}
              >
                x
              </button>
            </div>

            {/* Date display */}
            <div style={{
              padding: '16px',
              marginBottom: '20px',
              borderRadius: '12px',
              background: darkMode ? 'rgba(61, 90, 32, 0.08)' : 'rgba(61, 90, 32, 0.1)',
              border: '1px solid rgba(61, 90, 32, 0.2)'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '600', color: darkMode ? '#d9f99d' : '#15803d' }}>
                <Icon name="calendar" color="brand" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                {new Date(newBookingDateTime.date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              
              if (!newBookingFormData.bike_id || !newBookingFormData.template_id) {
                setError('Please select a bike and service type');
                return;
              }
              
              setSavingBooking(true);
              try {
                await bookingAPI.createBooking(company.id, {
                  bike_id: parseInt(newBookingFormData.bike_id),
                  service_template_id: parseInt(newBookingFormData.template_id),
                  booking_date: newBookingDateTime.date,
                  start_time: newBookingFormData.start_time,
                  technician_id: newBookingFormData.technician_id ? parseInt(newBookingFormData.technician_id) : null,
                  notes: newBookingFormData.notes || null
                });
                
                setShowAddBookingModal(false);
                setNewBookingDateTime(null);
                setNewBookingFormData({ bike_id: '', template_id: '', technician_id: '', start_time: '09:00', duration_minutes: 60, notes: '' });
                fetchData();
              } catch (err) {
                const apiError = err.response?.data?.error || 'Failed to create booking';
                const normalized = String(apiError).toLowerCase();
                if (normalized.includes('may not be available') || normalized.includes('no longer available')) {
                  setError('Selected slot is no longer available. Calendar was refreshed. Please choose another slot.');
                  fetchData();
                } else {
                  setError(apiError);
                }
              } finally {
                setSavingBooking(false);
              }
            }}>
              {/* Time Settings Row */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '12px', 
                marginBottom: '16px',
                padding: '16px',
                background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                borderRadius: '12px',
                border: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>
                    <Icon name="schedule" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={newBookingFormData.start_time}
                    onChange={(e) => setNewBookingFormData({...newBookingFormData, start_time: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '10px',
                      border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                      background: darkMode ? '#0a0a1a' : '#ffffff',
                      color: darkMode ? 'white' : '#1a1a1a',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px', fontWeight: '600' }}>
                    <Icon name="schedule" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    End Time
                  </label>
                  <div style={{
                    padding: '12px',
                    borderRadius: '10px',
                    background: darkMode ? 'rgba(61, 90, 32, 0.15)' : 'rgba(61, 90, 32, 0.1)',
                    border: '1px solid rgba(61, 90, 32, 0.3)',
                    color: darkMode ? '#4A8C18' : '#1A2811',
                    fontSize: '16px',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {(() => {
                      const [hours, minutes] = newBookingFormData.start_time.split(':').map(Number);
                      const totalMinutes = hours * 60 + minutes + newBookingFormData.duration_minutes;
                      const endHours = Math.floor(totalMinutes / 60) % 24;
                      const endMinutes = totalMinutes % 60;
                      return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
                    })()}
                    <span style={{ fontSize: '12px', opacity: 0.7, marginLeft: '8px' }}>
                      ({newBookingFormData.duration_minutes} min)
                    </span>
                  </div>
                </div>
              </div>

              {/* Bike Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  <Icon name="bike" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Bike *
                </label>
                <select
                  value={newBookingFormData.bike_id}
                  onChange={(e) => setNewBookingFormData({...newBookingFormData, bike_id: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? '#0a0a1a' : '#ffffff',
                    color: darkMode ? 'white' : '#1a1a1a',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select a bike --</option>
                  {bikes.map(bike => (
                    <option key={bike.id} value={bike.id}>
                      {bike.brand} {bike.model} - {bike.vin}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service Template Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  <Icon name="service" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Service Type *
                </label>
                <select
                  value={newBookingFormData.template_id}
                  onChange={(e) => {
                    const templateId = e.target.value;
                    const template = serviceTemplates.find(t => t.id === parseInt(templateId));
                    setNewBookingFormData({
                      ...newBookingFormData, 
                      template_id: templateId,
                      duration_minutes: template ? template.estimated_duration_minutes : 60
                    });
                  }}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? '#0a0a1a' : '#ffffff',
                    color: darkMode ? 'white' : '#1a1a1a',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select service type --</option>
                  {serviceTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.estimated_duration_minutes} min) {template.default_price ? `- GBP ${template.default_price.toLocaleString()}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Duration Override */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  <Icon name="schedule" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Duration (minutes) - adjustable
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[15, 20, 30, 45, 60, 90, 120].map(mins => (
                    <button
                      key={mins}
                      type="button"
                      onClick={() => setNewBookingFormData({...newBookingFormData, duration_minutes: mins})}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: newBookingFormData.duration_minutes === mins 
                          ? 'linear-gradient(135deg, #1A2811 0%, #4A8C18 100%)'
                          : darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                        color: newBookingFormData.duration_minutes === mins 
                          ? '#ffffff'
                          : darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                        transition: 'all 0.2s'
                      }}
                    >
                      {mins} min
                    </button>
                  ))}
                  <input
                    type="number"
                    value={newBookingFormData.duration_minutes}
                    onChange={(e) => setNewBookingFormData({...newBookingFormData, duration_minutes: parseInt(e.target.value) || 30})}
                    min="5"
                    max="480"
                    style={{
                      width: '80px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                      background: darkMode ? '#0a0a1a' : '#ffffff',
                      color: darkMode ? 'white' : '#1a1a1a',
                      fontSize: '13px',
                      textAlign: 'center'
                    }}
                  />
                </div>
              </div>

              {/* Technician Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  <Icon name="technician" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Technician (optional - auto if empty)
                </label>
                <select
                  value={newBookingFormData.technician_id}
                  onChange={(e) => setNewBookingFormData({...newBookingFormData, technician_id: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? '#0a0a1a' : '#ffffff',
                    color: darkMode ? 'white' : '#1a1a1a',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Automatic assignment</option>
                  {technicians.filter(t => t.is_active).map(tech => (
                    <option key={tech.id} value={tech.id}>
                      {tech.name} {tech.specializations ? `(${tech.specializations})` : ''}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: '4px' }}>
                  If left empty, the system will automatically assign the least busy technician.
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>
                  <Icon name="write" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Notes (optional)
                </label>
                <textarea
                  value={newBookingFormData.notes}
                  onChange={(e) => setNewBookingFormData({...newBookingFormData, notes: e.target.value})}
                  rows={2}
                  placeholder="Notes..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? '#0a0a1a' : '#ffffff',
                    color: darkMode ? 'white' : '#1a1a1a',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  disabled={savingBooking}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: savingBooking ? 'not-allowed' : 'pointer',
                    background: 'linear-gradient(135deg, #1A2811 0%, #4A8C18 100%)',
                    color: '#ffffff',
                    fontSize: '15px',
                    fontWeight: '700',
                    opacity: savingBooking ? 0.7 : 1
                  }}
                >
                  {savingBooking ? (
                    <>
                      <Icon name="loading" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Icon name="success" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                      Create Booking
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddBookingModal(false);
                    setNewBookingDateTime(null);
                    setNewBookingFormData({ bike_id: '', template_id: '', technician_id: '', start_time: '09:00', duration_minutes: 60, notes: '' });
                  }}
                  style={{
                    padding: '14px 20px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    background: 'transparent',
                    color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Catch-up Day Modal */}
      {showCatchUpModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '20px'
          }}
          onClick={() => setShowCatchUpModal(false)}
        >
          <div
            style={{
              background: darkMode ? '#0a0a1a' : '#ffffff',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: darkMode ? '#ffffff' : '#111827' }}>
                <Icon name="service" color="primary" darkMode={darkMode} size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Catch-up Day
              </h2>
              <button
                onClick={() => setShowCatchUpModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'
                }}
              >
                x
              </button>
            </div>

            <div style={{
              padding: '16px',
              background: darkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              borderRadius: '12px',
              marginBottom: '20px',
              fontSize: '14px',
              color: darkMode ? '#93c5fd' : '#1e40af'
            }}>
              <Icon name="info" color="info" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              <strong>What is a Catch-up Day?</strong> A day reserved for administrative tasks, paperwork, shop maintenance, or other internal work that does not require a bike. No bike or service needs to be linked. Use it to block the calendar and keep that day free for non-customer work.
            </div>

            {/* Date Selection */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Catch-up Day Date
              </label>
              <input
                type="date"
                value={catchUpDate}
                onChange={(e) => setCatchUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db',
                  background: darkMode ? '#0a0a1a' : '#ffffff',
                  color: darkMode ? '#ffffff' : '#111827',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Recent Services - optional, for reference only */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '15px', 
                color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151' 
              }}>
                Recent Services (optional - add to notes if planning follow-up)
              </h3>
              
              {loadingPendingServices ? (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' 
                }}>
                  Loading...
                </div>
              ) : pendingServices.length === 0 ? (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' 
                }}>
                  No recent services. You can still create a catch-up day without selecting any - just pick the date above.
                </div>
              ) : (
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {pendingServices.map(service => (
                    <div
                      key={service.id}
                      onClick={() => togglePendingServiceSelection(service.id)}
                      style={{
                        padding: '12px',
                        marginBottom: '8px',
                        background: selectedPendingServices.includes(service.id) 
                          ? (darkMode ? 'rgba(61, 90, 32, 0.2)' : 'rgba(61, 90, 32, 0.15)')
                          : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                        border: selectedPendingServices.includes(service.id)
                          ? '1px solid rgba(61, 90, 32, 0.4)'
                          : (darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'),
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ 
                            fontWeight: '500', 
                            color: darkMode ? '#ffffff' : '#111827',
                            marginBottom: '4px'
                          }}>
                            {service.bike_brand} {service.bike_model}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' 
                          }}>
                            {service.description?.substring(0, 50)}...
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af',
                            marginTop: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Icon name="calendar" color="secondary" darkMode={darkMode} size={12} />
                            {new Date(service.service_date).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          border: selectedPendingServices.includes(service.id)
                            ? '2px solid #3d5a20'
                            : (darkMode ? '2px solid rgba(255,255,255,0.2)' : '2px solid rgba(0,0,0,0.2)'),
                          background: selectedPendingServices.includes(service.id) ? '#3d5a20' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}>
                          {selectedPendingServices.includes(service.id) && (
                            <Icon name="success" customColor="#ffffff" darkMode={darkMode} size={12} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Count */}
            {selectedPendingServices.length > 0 && (
              <div style={{
                padding: '12px',
                background: darkMode ? 'rgba(61, 90, 32, 0.15)' : 'rgba(61, 90, 32, 0.1)',
                border: '1px solid rgba(61, 90, 32, 0.3)',
                borderRadius: '10px',
                marginBottom: '20px',
                fontSize: '14px',
                color: darkMode ? '#d9f99d' : '#3d5a20'
              }}>
                <Icon name="success" color="success" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                {selectedPendingServices.length} services selected for notes
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCatchUpModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={createCatchUpBookings}
                className="btn btn-primary"
                disabled={creatingCatchUp}
              >
                <Icon name="service" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                {creatingCatchUp ? 'Creating...' : 'Create Catch-up Day'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {passportBike && user && (
        <BikePassportModal
          bikeId={passportBike.id}
          bikeLabel={[passportBike.brand, passportBike.model].filter(Boolean).join(' ') || undefined}
          onClose={() => setPassportBike(null)}
          darkMode={darkMode}
          user={user}
          onLogout={onLogout}
          onToggleDarkMode={onToggleDarkMode}
        />
      )}
    </Layout>
  );
}

export default CompanyBookingsCalendar;
